import { Box, FormHelperText, InputAdornment, IconButton, Tabs, Tab, Paper } from "@mui/material";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LoginError, UserData, LoginHelperText, LoginResponse } from "../types/user";
import { loginAdmin, loginPartner } from "../apis/authentication";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const REGEX = {
  EMAIL_REGEX: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
};
const sampleLoginError = {
  email: false,
  password: false,
};
const sampleObject = {
  email: "",
  password: "",
};
const loginHelperObject = {
  email: "Valid email is required",
  password: "Password is required",
};

const Login = () => {
  const [loginTab, setLoginTab] = useState<number>(0); // 0: Admin/Agent/BackOffice, 1: Partner/PartnerAgent
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>("");
  const [loginError, setLoginError] = useState<LoginError>(sampleLoginError);
  const [loginHelperText] = useState<LoginHelperText>(loginHelperObject);
  const [loginField, setLoginField] = useState<UserData>(sampleObject);
  const navigate = useNavigate();

  const mainContainer: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    margin: "auto",
  };
  const innerSubContainer: React.CSSProperties = {
    margin: "50px",
    padding: "50px",
    width: "40%",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0px 10px 60px rgba(226, 236, 249, 0.5)",
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  // Method to handle the inputs
  const onHandleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    // Convert email to lowercase as user types
    const processedValue = name === "email" ? value.toLowerCase() : value;
    setLoginField({ ...loginField, [name]: processedValue });
    checkLoginValidation(name, processedValue);
  };

  // Method to validate the input fields
  const checkLoginValidation = (name: string, value: string) => {
    switch (name) {
      case "email":
        const isEmail = REGEX.EMAIL_REGEX.test(value);
        setLoginError({ ...loginError, [name]: !isEmail });
        break;
      case "password":
        const isPass = value.length > 3;
        setLoginError({ ...loginError, [name]: !isPass });
        break;
      default:
        break;
    }
  };

  // Method to handle the login action and API request
  const onHandleSubmit = async () => {
    if (loginField.password.length < 4) {
      setLoginError({ ...loginError, password: true });
      return;
    }

    if (!REGEX.EMAIL_REGEX.test(loginField.email)) {
      setLoginError({ ...loginError, email: true });
      return;
    }

    try {
      setLoading(true);
      setErrorText(""); // Clear previous errors
      const data: UserData = {
        email: loginField.email.toLowerCase().trim(),
        password: loginField.password,
      };

      // Call appropriate login API based on selected tab
      let response: LoginResponse | undefined;
      if (loginTab === 0) {
        // Admin/Agent/BackOffice login
        response = await loginAdmin(data);
      } else {
        // Partner/PartnerAgent login
        response = await loginPartner(data);
      }

      if (response && (response.status === 201 || response.status === 200)) {
        localStorage.setItem("token", response.data.accessToken);
        navigate("/");
      } else {
        // Handle error response
        const errorMessage =
          (response as any)?.response?.data?.message ||
          (response as any)?.message ||
          "Invalid credentials. Please try again.";
        setErrorText(errorMessage);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      console.error("Login error:", error);
      if (error.response) {
        setErrorText(error.response.data.message || "Invalid credentials. Please try again.");
      } else if (error.request) {
        setErrorText("No response from server. Please try again.");
      } else {
        setErrorText("An error occurred. Please try again.");
      }
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setLoginTab(newValue);
    setErrorText(""); // Clear error when switching tabs
    setLoginField({ email: "", password: "" }); // Clear form when switching tabs
  };

  return (
    <Box sx={mainContainer}>
      <Box sx={innerSubContainer}>
        <h1 style={{ textAlign: "center", marginBottom: "20px" }}>Log In</h1>
        
        {/* Tabs for selecting login type */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={loginTab} onChange={handleTabChange} variant="fullWidth">
            <Tab label="Teracar Admin/Agent/BackOffice" />
            <Tab label="Teracar Partner/PartnerAgent" />
          </Tabs>
        </Paper>

        {errorText && (
          <FormHelperText sx={{ color: "red", marginBottom: "10px", textAlign: "center" }}>
            {errorText}
          </FormHelperText>
        )}
        
        <TextField
          id="outlined-basic"
          placeholder="Email"
          name="email"
          variant="outlined"
          style={{ marginBottom: "10px" }}
          error={loginError.email}
          value={loginField.email}
          onChange={onHandleChange}
          onBlur={(event) => checkLoginValidation(event.target.name, event.target.value)}
          fullWidth
        />
        {loginError.email && (
          <FormHelperText sx={{ color: "red" }}>{loginHelperText.email}</FormHelperText>
        )}
        
        <TextField
          id="outlined-basic"
          placeholder="Password"
          name="password"
          variant="outlined"
          type={showPassword ? "text" : "password"}
          InputProps={{
            endAdornment: (
              <InputAdornment position="start">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          style={{ marginBottom: "10px" }}
          error={loginError.password}
          value={loginField.password}
          onChange={onHandleChange}
          onBlur={(event) => checkLoginValidation(event.target.name, event.target.value)}
          fullWidth
        />
        {loginError.password && (
          <FormHelperText sx={{ color: "red" }}>{loginHelperText.password}</FormHelperText>
        )}

        <Button
          variant="contained"
          fullWidth
          onClick={onHandleSubmit}
          disabled={loading}
          sx={{ mt: 2 }}
        >
          {loading ? "Logging in..." : "Log In"}
        </Button>
      </Box>
    </Box>
  );
};

export default Login;
